import React from "react";
import RecipeSelect from "../RecipeSelect.js/RecipeSelect";
import {
  GoFile,
  GoTriangleUp,
  GoTriangleDown,
  GoX,
  GoEye,
  GoEyeClosed,
  GoTrashcan,
} from "react-icons/go";
import cx from "classnames";
import { memoize } from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  aggregateIngredientCount,
  FOOD_LABEL,
  getIngredients,
} from "../../helpers";
import "./TripPlan.css";

const PLACEHOLDER_TEXT = "Remark";
const HEADER_PLACEHOLDER_TEXT = "Header";
const VIEW_COMPOSITES_TEXT = "View as Recipe";
const VIEW_RAW_MATERIALS_TEXT = "View as Raw Materials";
const DELETE_TRIP_TEXT = "Remove Trip";

const getIngredientsMemoized = memoize(
  getIngredients,
  (itemId, base) => `${itemId}_base_${base}`
);

export default function TripPlan({
  id,
  name,
  onChange,
  onRemove,
  initialRemark = "",
  initialRecipes = [],
  initalCountMap = {},
  toggleIngredientsView = false,
  disableRemove = false,
}) {
  const [headerName, setHeaderName] = React.useState(name);
  const [remark, setRemark] = React.useState(initialRemark);

  const [selectedRecipes, setSelectedRecipes] = React.useState(initialRecipes);
  const [recipeCount, setRecipeCount] = React.useState(initalCountMap);
  const [toggleBaseIngredientsView, setToggleBaseIngredientsView] =
    React.useState(toggleIngredientsView);

  const recipeMap = React.useMemo(() => {
    return selectedRecipes.reduce((map, recipe) => {
      return Object.assign(map, {
        [recipe.id]: getIngredientsMemoized(
          recipe.id,
          toggleBaseIngredientsView
        ).map((i) => ({ ...i, count: i.count * recipeCount[recipe.id] })),
      });
    }, {});
  }, [selectedRecipes, recipeCount, toggleBaseIngredientsView]);

  const aggregatedIngredients = React.useMemo(() => {
    return aggregateIngredientCount(
      Object.values(recipeMap).reduce((prev, curr) => [...prev, ...curr], [])
    );
  }, [recipeMap]);

  React.useEffect(() => {
    onChange({
      id: id,
      header: headerName,
      remark: remark,
      recipes: selectedRecipes.map((r) => r.id),
      recipeCount: selectedRecipes.map((r) => recipeCount[r.id]),
      toggleIngredientsView: toggleBaseIngredientsView,
    });
  }, [
    id,
    onChange,
    headerName,
    remark,
    selectedRecipes,
    recipeCount,
    toggleBaseIngredientsView,
  ]);

  return (
    <div className="Plan bg-white drop-shadow p-4 rounded-md rounded-t-none">
      <div className="mb-4 flex flex-col items-stretch md:flex-row justify-between md:items-center">
        <div className="mb-4 md:mb-0 md:flex-1 md:mr-4">
          <input
            placeholder={HEADER_PLACEHOLDER_TEXT}
            className="capitalize text-left text-blue-900 font-extrabold text-xl w-full outline-none placeholder:text-neutral-500"
            value={headerName}
            onChange={(e) => setHeaderName(e.target.value)}
          />
          <div className="text-neutral-500 text-sm flex items-start mt-1">
            <GoFile className="mr-1 h-5" />
            <textarea
              rows={1}
              placeholder={PLACEHOLDER_TEXT}
              className="w-full outline-1 outline-dashed outline-gray-400 px-1 transition-all duration-150 placeholder:text-neutral-500"
              value={remark}
              onChange={(e) => {
                e.target.style.height = "inherit";
                e.target.style.height = `${e.target.scrollHeight}px`;
                setRemark(e.target.value);
              }}
            />
          </div>
        </div>
        <div className="flex items-center">
          <button
            disabled={disableRemove}
            className={cx(
              "mr-2 flex-1 flex flex-col self-stretch justify-center py-3 px-6 md:w-52 md:flex-row w-auto items-center text-white bg-red-500 font-semibold text-sm rounded-md transition duration-500",
              { "bg-red-600": toggleBaseIngredientsView },
              { "bg-gray-600": disableRemove }
            )}
            onClick={() => onRemove(id)}
          >
            <GoTrashcan className="h-4 w-4 md:mr-2" />
            <span className="capitalize font-medium">{DELETE_TRIP_TEXT}</span>
          </button>
          <button
            className={cx(
              "flex flex-1 flex-col self-stretch justify-center py-3 px-6 md:w-52 md:h-full md:flex-row w-auto items-center text-white bg-blue-500 font-semibold text-sm rounded-md transition duration-500",
              { "bg-blue-600": toggleBaseIngredientsView }
            )}
            onClick={() => setToggleBaseIngredientsView((prev) => !prev)}
          >
            {toggleBaseIngredientsView ? (
              <GoEyeClosed className="h-4 w-4 md:mr-2" />
            ) : (
              <GoEye className="h-4 w-4 md:mr-2" />
            )}
            <span className="capitalize font-medium">
              {toggleBaseIngredientsView
                ? VIEW_COMPOSITES_TEXT
                : VIEW_RAW_MATERIALS_TEXT}
            </span>
          </button>
        </div>
      </div>

      <RecipeSelect
        filterOptions={selectedRecipes}
        onSelect={(data) => {
          setSelectedRecipes((prev) => [...prev, data]);
          setRecipeCount((prev) => ({ ...prev, [data.id]: 1 }));
        }}
      />

      <div className="flex justify-center my-4 flex-wrap">
        {aggregatedIngredients.map((ingredient) => (
          <div key={ingredient.id} className="flex items-center text-sm mr-4">
            <img
              className="h-auto w-7 mr-px"
              src={ingredient.icon}
              alt={ingredient.name}
              title={ingredient.name}
            />
            <GoX className="w-4 h-4" />
            <span className="font-bold">{ingredient.count}</span>
          </div>
        ))}
      </div>

      <DragDropContext
        onDragEnd={(result) => {
          if (result.destination) {
            const newRecipes = Array.from(selectedRecipes);
            const [removed] = newRecipes.splice(result.source.index, 1);
            newRecipes.splice(result.destination.index, 0, removed);
            setSelectedRecipes(newRecipes);
          }
        }}
      >
        <Droppable droppableId="recipe-container">
          {(provided) => (
            <div
              {...provided.droppableProps}
              className="Breakdown divide-y divide-neutral-300 overflow-y-auto"
              ref={provided.innerRef}
            >
              {selectedRecipes.map((recipe, index) => (
                <Draggable
                  key={recipe.id}
                  draggableId={recipe.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      {...provided.dragHandleProps}
                      {...provided.draggableProps}
                      ref={provided.innerRef}
                      className={cx(
                        "flex items-center py-2 hover:bg-gray-200",
                        {
                          "Recipe--dragging bg-white drop-shadow-md rounded-sm":
                            snapshot.isDragging,
                        }
                      )}
                      style={
                        !snapshot.isDropAnimating
                          ? provided.draggableProps.style
                          : {
                              ...provided.draggableProps.style,
                              transitionDuration: "0.001s",
                            }
                      }
                    >
                      <img
                        className="h-auto w-8"
                        src={recipe.icon}
                        alt={recipe.name}
                        title={recipe.name}
                      />
                      <span className="flex-1 text-left font-semibold text-sm mx-2">
                        {recipe.isFood && (
                          <span className="uppercase text-xs text-white font-bold mr-1 bg-orange-500 rounded-sm px-2 py-px">
                            {FOOD_LABEL}
                          </span>
                        )}
                        {recipe.name}
                      </span>
                      <div className="inline-flex flex-wrap items-center justify-end">
                        {recipeMap[recipe.id].map((ingredient) => (
                          <div
                            key={ingredient.id}
                            className="flex items-center text-sm mr-4"
                          >
                            <img
                              className="h-auto w-7 mr-px"
                              src={ingredient.icon}
                              alt={ingredient.name}
                              title={ingredient.name}
                            />
                            <GoX className="w-4 h-4" />
                            <span className="font-bold">
                              {ingredient.count}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center font-semibold">
                        <button
                          className="hover:text-neutral-500"
                          onClick={() => {
                            setRecipeCount((prev) => ({
                              ...prev,
                              [recipe.id]: prev[recipe.id] + 1,
                            }));
                          }}
                        >
                          <GoTriangleUp className="h-5 w-5" />
                        </button>
                        <span className="mx-0.5">{recipeCount[recipe.id]}</span>
                        <button
                          className="hover:text-neutral-500"
                          onClick={() => {
                            if (recipeCount[recipe.id] > 1) {
                              setRecipeCount((prev) => ({
                                ...prev,
                                [recipe.id]: prev[recipe.id] - 1,
                              }));
                            } else {
                              setSelectedRecipes((prev) =>
                                prev.filter((r) => r.id !== recipe.id)
                              );
                            }
                          }}
                        >
                          <GoTriangleDown className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
